import AccountServiceClient from './accounts';
import AuditlogAdminClient from './auditlogAdminClient';
import AuditlogWriteClient from './auditlogWriteClient';
import AuthenticatorServiceClient from './authenticators';
import ChannelServiceClient from './channels';
import ChildOrganizationServiceClient from './childOrganizations';
import CredentialServiceClient from './credentials';
import { AccountService as EspbAccountService } from './espb/account_service_pb';
import { AuthenticatorService as EspbAuthenticatorService } from './espb/authenticator_service_pb';
import { ChannelService as EspbChannelService } from './espb/channel_service_pb';
import { ChildOrganizationService as EspbChildOrganizationService } from './espb/child_organization_service_pb';
import { CredentialService as EspbCredentialService } from './espb/credential_service_pb';
import { HealthService as EspbHealthService } from './espb/health_service_pb';
import { IntegrationsService as EspbIntegrationsService } from './espb/integrations_service_pb';
import { LFDOverridesService as EspbLFDOverridesService } from './espb/lfd_overrides_service_pb';
import { LoginInboxService as EspbLoginInboxService } from './espb/login_inbox_service_pb';
import { OrganizationService as EspbOrganizationService } from './espb/organization_service_pb';
import { ProfileService as EspbProfileService } from './espb/profile_service_pb';
import { SessionService as EspbSessionService } from './espb/session_service_pb';
import { ShareLinkService as EspbShareLinkService } from './espb/share_link_service_pb';
import { SupportService as EspbSupportService } from './espb/support_service_pb';
import { SyncService as EspbSyncService } from './espb/sync_service_pb';
import { UserConfirmationService as EspbUserConfirmationService } from './espb/user_confirmation_service_pb';
import { VaultService as EspbVaultService } from './espb/vault_service_pb';
import { WebauthnService as EspbWebauthnService } from './espb/webauthn_service_pb';
import EspbServiceClientFactory, { AuthTokens } from './EspbServiceClientFactory';
import HealthServiceClient from './health';
import IntegrationsServiceClient from './integrations';
import LFDOverridesClient from './lfdOverrides';
import LFDOverridesAdminClient from './lfdOverridesAdminClient';
import LoginInboxServiceClient from './loginInbox';
import OrganizationServiceClient from './organizations';
import ProfileServiceClient from './profiles';
import SessionServiceClient from './sessions';
import ShareLinkServiceClient from './shareLinks';
import SupportServiceClient from './support';
import { SyncServiceClient } from './sync';
import {
  LoginData,
  SessionId,
  AuditlogAdminToken,
  AuditlogWriteToken,
  LFDOverridesAdminToken,
  ClientType,
  UserId,
} from './types';
import UserConfirmationServiceClient from './userConfirmations';
import VaultServiceClient from './vaults';
import WebauthnServiceClient from './webauthn';

interface BackendClientConfig {
  readonly backendBaseUri: string;
  readonly auditlogBaseUri: string;
  readonly clientType: ClientType;
  readonly loginData?: LoginData;
  readonly clientId?: string;
  readonly clientVersion?: string;
  readonly syncVersion?: number;
  readonly syncSuperUsers: boolean;
  readonly clientOperationId?: string;
  readonly clientOperationName?: string;
}

export default class BackendClient {
  public readonly config: BackendClientConfig;

  public readonly account: AccountServiceClient;

  public readonly credential: CredentialServiceClient;

  public readonly vault: VaultServiceClient;

  public readonly authenticator: AuthenticatorServiceClient;

  public readonly channel: ChannelServiceClient;

  public readonly sync: SyncServiceClient;

  public readonly session: SessionServiceClient;

  public readonly support: SupportServiceClient;

  public readonly organization: OrganizationServiceClient;

  public readonly childOrganization: ChildOrganizationServiceClient;

  public readonly profile: ProfileServiceClient;

  public readonly loginInbox: LoginInboxServiceClient;

  public readonly integrations: IntegrationsServiceClient;

  public readonly webauthn: WebauthnServiceClient;

  public readonly lfdOverrides: LFDOverridesClient;

  public readonly health: HealthServiceClient;

  public readonly shareLinks: ShareLinkServiceClient;

  public readonly userConfirmations: UserConfirmationServiceClient;

  private readonly espbClientFactory: EspbServiceClientFactory;

  constructor(
    config: Omit<BackendClientConfig, 'auditlogBaseUri' | 'syncSuperUsers'> &
      Partial<BackendClientConfig>,
  ) {
    this.config = {
      auditlogBaseUri: config.backendBaseUri,
      syncSuperUsers: false,
      ...config,
    };

    const authTokens: AuthTokens = {
      backend: this.config.loginData?.token,
    };

    this.espbClientFactory = new EspbServiceClientFactory({
      authTokens,
      clientId: this.config.clientId,
      clientType: this.config.clientType,
      clientVersion: this.config.clientVersion,
      baseUri: this.config.backendBaseUri,
      clientOperationId: this.config.clientOperationId,
      clientOperationName: this.config.clientOperationName,
      syncVersion: this.config.syncVersion,
      syncSuperUsers: this.config.syncSuperUsers,
    });

    this.account = new AccountServiceClient(
      this.espbClientFactory.createServiceClient(EspbAccountService),
    );
    this.credential = new CredentialServiceClient(
      this.espbClientFactory.createServiceClient(EspbCredentialService),
    );
    this.vault = new VaultServiceClient(
      this.espbClientFactory.createServiceClient(EspbVaultService),
    );
    this.authenticator = new AuthenticatorServiceClient(
      this.espbClientFactory.createServiceClient(EspbAuthenticatorService),
    );
    this.channel = new ChannelServiceClient(
      this.espbClientFactory.createServiceClient(EspbChannelService),
    );
    this.sync = new SyncServiceClient(this.espbClientFactory.createServiceClient(EspbSyncService));
    this.session = new SessionServiceClient(
      this.espbClientFactory.createServiceClient(EspbSessionService),
    );
    this.support = new SupportServiceClient(
      this.espbClientFactory.createServiceClient(EspbSupportService),
    );
    this.organization = new OrganizationServiceClient(
      this.espbClientFactory.createServiceClient(EspbOrganizationService),
    );
    this.profile = new ProfileServiceClient(
      this.espbClientFactory.createServiceClient(EspbProfileService),
    );
    this.loginInbox = new LoginInboxServiceClient(
      this.espbClientFactory.createServiceClient(EspbLoginInboxService),
    );
    this.integrations = new IntegrationsServiceClient(
      this.espbClientFactory.createServiceClient(EspbIntegrationsService),
    );
    this.webauthn = new WebauthnServiceClient(
      this.espbClientFactory.createServiceClient(EspbWebauthnService),
    );
    this.lfdOverrides = new LFDOverridesClient(
      this.espbClientFactory.createServiceClient(EspbLFDOverridesService),
    );
    this.health = new HealthServiceClient(
      this.espbClientFactory.createServiceClient(EspbHealthService),
    );
    this.shareLinks = new ShareLinkServiceClient(
      this.espbClientFactory.createServiceClient(EspbShareLinkService),
    );
    this.userConfirmations = new UserConfirmationServiceClient(
      this.espbClientFactory.createServiceClient(EspbUserConfirmationService),
    );
    this.childOrganization = new ChildOrganizationServiceClient(
      this.espbClientFactory.createServiceClient(EspbChildOrganizationService),
    );
  }

  withLogin(loginData: LoginData): BackendClient {
    return this.withConfig({ loginData });
  }

  withClientId(clientId: string | undefined): BackendClient {
    return this.withConfig({ clientId });
  }

  withSyncVersion(syncVersion: number | undefined) {
    if (syncVersion === this.config.syncVersion) {
      return this;
    }

    return this.withConfig({ syncVersion });
  }

  withSyncSuperUsers(syncSuperUsers: boolean): BackendClient {
    if (syncSuperUsers === this.config.syncSuperUsers) {
      return this;
    }

    return this.withConfig({ syncSuperUsers });
  }

  withClientOperation(
    clientOperationId: string,
    clientOperationName: string,
    syncVersion: number | undefined,
  ): BackendClient {
    return this.withConfig({ clientOperationId, clientOperationName, syncVersion });
  }

  private withConfig(newConfig: Partial<BackendClientConfig>) {
    return new BackendClient({ ...this.config, ...newConfig });
  }

  getSessionId(): SessionId {
    return this.ensureLoginData().sessionId;
  }

  getUserId(): UserId {
    return this.ensureLoginData().userId;
  }

  private ensureLoginData(): LoginData {
    if (!this.config.loginData) {
      throw new Error('Login Data is missing!');
    }
    return this.config.loginData;
  }

  async deleteSession(sessionId: SessionId) {
    return this.session.deleteSession(sessionId);
  }

  getAuditlogWriteClient(authToken: AuditlogWriteToken) {
    return new AuditlogWriteClient(
      this.config.auditlogBaseUri,
      authToken.token,
      this.config.clientType,
      this.config.clientId,
      this.config.clientVersion,
    );
  }

  getAuditlogAdminClient(authToken: AuditlogAdminToken) {
    return new AuditlogAdminClient(
      this.config.auditlogBaseUri,
      authToken.token,
      this.config.clientType,
      this.config.clientId,
      this.config.clientVersion,
    );
  }

  getLFDOverridesAdminClient(authToken: LFDOverridesAdminToken) {
    return new LFDOverridesAdminClient(
      this.config.backendBaseUri,
      authToken.token,
      this.config.clientType,
      this.config.clientId,
      this.config.clientVersion,
    );
  }
}
